import ProjRef from "@/shared/components/ProjRef";
import { useEffect, useState, Fragment } from "react";
import api from "@/shared/lib/api";
import { fmtISTNice } from "@/shared/lib/time";
import { PageHeader } from "@/shared/components/UI";
import { Activity, Users, CalendarCheck, ChevronDown, ChevronRight, FolderOpen, History, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

function MonthlyExport() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [busy, setBusy] = useState(false);
  const years = [];
  for (let y = now.getFullYear(); y >= 2025; y--) years.push(y);
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/activity/export?year=${year}&month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `MIPL Activity - ${MONTHS[month - 1]} ${year}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Activity report for ${MONTHS[month - 1]} ${year} downloaded`);
    } catch { toast.error("Could not generate the report"); }
    finally { setBusy(false); }
  };
  return (
    <div className="card-flat p-4 flex flex-wrap items-center gap-3" data-testid="activity-export-bar">
      <span className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
        <FileSpreadsheet className="w-4.5 h-4.5" />
      </span>
      <div className="flex-1 min-w-[180px]">
        <div className="text-sm font-semibold text-zinc-900">Monthly activity report</div>
        <div className="text-[11px] text-zinc-400">Per-user logins, projects & work log — one neat Excel</div>
      </div>
      <select value={month} onChange={(e) => setMonth(Number(e.target.value))}
              className="h-9 px-2.5 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-300"
              data-testid="activity-export-month">
        {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
      </select>
      <select value={year} onChange={(e) => setYear(Number(e.target.value))}
              className="h-9 px-2.5 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-300"
              data-testid="activity-export-year">
        {years.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
      <button onClick={download} disabled={busy}
              className="h-9 px-4 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              data-testid="activity-export-download">
        {busy ? "Preparing…" : "Download Excel"}
      </button>
    </div>
  );
}

const ROLE_LABEL = {
  admin: "Admin", management: "Management", scope_user: "Scope", csm: "CSM",
  psm: "PSM", ism: "ISM", designer: "Designer", prepress: "Pre-Press",
  pre_audit: "Pre-Audit", procurement: "Procurement", production: "Production",
  site_inspector: "Site Inspector", accounts: "Accounts", sales: "Sales", client: "Client",
};

function Stat({ icon: Icon, label, value, tint }) {
  return (
    <div className="card-flat p-5 flex items-center gap-4" data-testid={`activity-stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <span className={`w-11 h-11 rounded-xl flex items-center justify-center ${tint}`}><Icon className="w-5 h-5" /></span>
      <div>
        <div className="text-2xl font-bold tabular-nums text-zinc-900">{value}</div>
        <div className="text-xs text-zinc-500">{label}</div>
      </div>
    </div>
  );
}

export default function ActivityPanel() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState("");
  const [details, setDetails] = useState({});
  useEffect(() => { api.get("/admin/activity").then((r) => setData(r.data)).catch(() => {}); }, []);
  const toggle = (uid) => {
    const next = open === uid ? "" : uid;
    setOpen(next);
    if (next && !details[next]) {
      api.get(`/admin/activity/user/${next}`)
        .then((r) => setDetails((d) => ({ ...d, [next]: r.data })))
        .catch(() => setDetails((d) => ({ ...d, [next]: { projects: [], actions: [] } })));
    }
  };
  if (!data) return <div className="p-8 text-sm text-zinc-400">Loading activity…</div>;
  const max = Math.max(1, ...data.daily.map((d) => d.active));
  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="activity-panel">
      <PageHeader title="Portal Activity" subtitle="Who is using the portal, and when — logins shown in IST" />

      <MonthlyExport />

      <div className="grid sm:grid-cols-3 gap-4">
        <Stat icon={Activity} label="Active today" value={data.active_today} tint="bg-emerald-50 text-emerald-600" />
        <Stat icon={CalendarCheck} label="Active this week" value={data.active_week} tint="bg-sky-50 text-sky-600" />
        <Stat icon={Users} label="Total users" value={data.total_users} tint="bg-zinc-100 text-zinc-600" />
      </div>

      <div className="card-flat p-5">
        <div className="text-sm font-semibold text-zinc-900 mb-4">Daily active users · last 14 days</div>
        <div className="flex items-end gap-2 h-32" data-testid="activity-trend">
          {data.daily.map((d) => (
            <div key={d.date} className="flex-1 flex flex-col items-center gap-1.5 group">
              <span className="text-[10px] tabular-nums text-zinc-500">{d.active || ""}</span>
              <div className={`w-full rounded-t-md transition-colors ${d.active ? "bg-emerald-400 group-hover:bg-emerald-500" : "bg-zinc-100"}`}
                   style={{ height: `${Math.max(4, (d.active / max) * 100)}%` }} title={`${d.dm}: ${d.active} user(s)`} />
              <span className="text-[9px] text-zinc-400">{d.day[0]}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="card-flat overflow-hidden">
        <div className="px-5 py-4 text-sm font-semibold text-zinc-900 border-b border-zinc-100">Users · last login <span className="font-normal text-zinc-400">(click a user for projects & 30-day activity)</span></div>
        <table className="w-full text-sm">
          <tbody>
            {data.users.map((u) => (
              <Fragment key={u.id}>
                <tr className="border-b border-zinc-50 last:border-0 cursor-pointer hover:bg-zinc-50/60 transition-colors"
                    onClick={() => toggle(u.id)} data-testid={`activity-user-${u.id}`}>
                  <td className="px-5 py-3">
                    <div className="font-medium text-zinc-900 flex items-center gap-1.5">
                      {open === u.id ? <ChevronDown className="w-3.5 h-3.5 text-zinc-400" /> : <ChevronRight className="w-3.5 h-3.5 text-zinc-300" />}
                      {u.name}
                    </div>
                    <div className="text-[11px] text-zinc-400 ml-5">{u.email}</div>
                  </td>
                  <td className="px-3 py-3 text-xs text-zinc-500">{ROLE_LABEL[u.role] || u.role}</td>
                  <td className="px-3 py-3 text-xs text-zinc-600">
                    {u.last_login ? fmtISTNice(u.last_login) : <span className="text-zinc-300">Never logged in</span>}
                  </td>
                  <td className="px-5 py-3 text-right text-xs tabular-nums text-zinc-500">
                    {u.logins_30d ? `${u.logins_30d} login${u.logins_30d !== 1 ? "s" : ""} / 30d` : "—"}
                  </td>
                </tr>
                {open === u.id && (
                  <tr className="border-b border-zinc-100">
                    <td colSpan={4} className="px-5 pb-4 pt-1 bg-zinc-50/40">
                      <UserDetail detail={details[u.id]} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UserDetail({ detail }) {
  if (!detail) return <div className="text-xs text-zinc-400 py-3">Loading…</div>;
  const acts = detail.actions || [];
  return (
    <div className="grid md:grid-cols-2 gap-4 mt-2" data-testid="activity-user-detail">
      <div className="bg-white border border-zinc-200 rounded-lg p-3.5">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-2">
          <FolderOpen className="w-3.5 h-3.5" /> Projects ({(detail.projects || []).length})
        </div>
        {(detail.projects || []).length === 0 ? (
          <div className="text-xs text-zinc-400">Not assigned to any project.</div>
        ) : (
          <div className="space-y-1.5 max-h-52 overflow-y-auto">
            {detail.projects.map((p) => (
              <div key={p.project_code} className="flex items-center justify-between gap-2 text-xs" data-testid={`activity-proj-${p.project_code}`}>
                <span className="text-zinc-800 truncate">
                  <span className="font-mono text-[10px] text-orange-600 mr-1"><ProjRef code={p.project_code} /></span>{p.name}
                </span>
                <span className="text-[10px] text-zinc-400 whitespace-nowrap">{p.slots.join(", ")}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="bg-white border border-zinc-200 rounded-lg p-3.5">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-2">
          <History className="w-3.5 h-3.5" /> Last 30 days ({acts.length} action{acts.length !== 1 ? "s" : ""}{detail.logins_30d ? ` · ${detail.logins_30d} logins` : ""})
        </div>
        {acts.length === 0 ? (
          <div className="text-xs text-zinc-400">No recorded actions in the last 30 days.</div>
        ) : (
          <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
            {acts.map((a, i) => (
              <div key={i} className="flex items-start justify-between gap-2 text-xs">
                <span className="text-zinc-700">
                  <span className="capitalize">{(a.action || "").replace(/_/g, " ")}</span>
                  {a.material ? <span className="text-zinc-500"> — {a.material}</span> : null}
                  {a.project_code ? <span className="font-mono text-[10px] text-zinc-400 ml-1"><ProjRef code={a.project_code} /></span> : null}
                </span>
                <span className="text-[10px] text-zinc-400 whitespace-nowrap">{fmtISTNice(a.at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
